import { Trans, useTranslation } from "react-i18next";
import {
  formatNoSignObject,
  moneyFormatObject,
  signByCurrency,
} from "shared/helpers/BRLFormat";
import LogoAdvance from "shared/assets/images/icon-advance-initial.svg";
import ImagemSessaoIntermediaria from "shared/assets/images/adiantamento-sessao-intermediaria.png";
import { useSelector } from "react-redux";

import { ReactComponent as Clock } from "shared/assets/images/clock-eight.svg";
import { ReactComponent as AdiantamentoPassoUm } from "shared/assets/images/adiantamento-inicio-passo-um.svg";
import { ReactComponent as AdiantamentoPassoDois } from "shared/assets/images/adiantamento-inicio-passo-dois.svg";
import { ReactComponent as AdiantamentoPassoTres } from "shared/assets/images/adiantamento-inicio-passo-tres.svg";
import { ReactComponent as AdiantamentoPassoQuatro } from "shared/assets/images/adiantamento-inicio-passo-quatro.svg";
import PurpleCircle from "shared/assets/images/icon-circle-purple.svg";

import { CashAdvanceAvailableEnum } from "shared/enums/CashAdvanceAvailable";
import { getSpecialCampaign } from "shared/helpers/special-campaign-helpers";
import { SpecialCampaignEnum } from "shared/enums/SpecialCampaignEnum";
import { ClientSpecialCampaignApprovalEnum } from "shared/enums/ClientSpecialCampaignApprovalEnum";
import {
  dataLayerClickedEvent,
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { canAskAdvance } from "explorer/reducers/adiantamentos";
import { useEffect } from "react";
import { AdvanceButtonClickEventName } from "event-tracker/button-click-event-name";
import { AdvanceWrapper } from "shared/components/smart-wrapper/SmartWrapper";
import { formatAdvanceMinimumAmount } from "shared/helpers/cash-advance-helpers";
import "./AdiantamentoInicio.scss";
import { ArtistFeatureCard } from "shared/components/artist-feature-card/ArtistFeatureCard";
import { motion } from "motion/react";

interface AdiantamentoInicioProps {
  createAdvance: any;
}
const AdiantamentoInicio: React.FC<AdiantamentoInicioProps> = (props) => {
  const { createAdvance } = props;
  const { t } = useTranslation();
  const { loggedUser } = useSelector((state: any) => state.users);
  const noQuiz = loggedUser?.userArtists?.length < 1;

  const { userArtist, loading } = useSelector((state: any) => state.artists);
  const { cashAdvanceAvailable, loading: advanceLoading } = useSelector(
    (state: any) => state.advances,
  );

  const advanceAmount = `${signByCurrency(
    userArtist?.cashAdvanceEstimated?.currency,
  )} ${formatNoSignObject({
    currency: userArtist?.cashAdvanceEstimated?.currency,
    amount:
      userArtist?.cashAdvanceEstimated?.amount /
      userArtist?.cashAdvanceEstimated?.trancheFactor,
  })}`;

  const specialCampaignHenriqueJuliano = getSpecialCampaign(
    userArtist?.specialCampaign,
    SpecialCampaignEnum.HenriqueEJuliano,
  );
  const specialCampaignKamillaFialho = getSpecialCampaign(
    userArtist?.specialCampaign,
    SpecialCampaignEnum.KamillaFialho,
  );
  const specialCampaignAnnoDomini = getSpecialCampaign(
    userArtist?.specialCampaign,
    SpecialCampaignEnum.AnnoDomini,
  );

  const specialCampaign =
    specialCampaignHenriqueJuliano ||
    specialCampaignKamillaFialho ||
    specialCampaignAnnoDomini;

  const onClickIniciaProcesso = async () => {
    createAdvance(true);
    dataLayerClickedEvent(AdvanceButtonClickEventName.REQUEST, {
      ...tagManagerEvent.Advance.S4A_ADVANCE_INDEX,
      carreerStage: userArtist?.stage,
      approvedLimit: userArtist?.cashAdvanceEstimated?.amountUsd,
      alreadyCalculated:
        userArtist?.cashAdvanceEstimated?.successfullyEstimated,
      country: userArtist?.country,
    });
  };

  const mensagemSemAcesso = () => {
    if (
      cashAdvanceAvailable === CashAdvanceAvailableEnum.NotAvailable ||
      cashAdvanceAvailable === CashAdvanceAvailableEnum.NotFound
    )
      return t("pages.Adiantamentos.naoCumpreRequisitosMensagem", {
        amount: formatAdvanceMinimumAmount({
          country: userArtist?.country,
          currency: userArtist?.cashAdvanceEstimated?.currency,
        }),
      });

    if (cashAdvanceAvailable === CashAdvanceAvailableEnum.NeedRetry)
      return t("pages.Adiantamentos.tentarNovamenteEmbreve");

    // if (cashAdvanceAvailable === CashAdvanceAvailableEnum.NotFound)
    //   return t("pages.Adiantamentos.spotifyNaoEncontrado");

    if (!loggedUser?.cashAdvanceFeatureAvailable)
      return t("pages.Adiantamentos.funcionalidadeNaoDisponivel");

    if (noQuiz) return t("pages.Adiantamentos.semArtista");

    if (!userArtist?.spotifyId)
      return t("pages.Adiantamentos.artistaSemSpotify");

    if (specialCampaign?.approval === ClientSpecialCampaignApprovalEnum.Pending)
      return t("pages.Adiantamentos.campanhaEspecialPendente");
  };

  const carregando: boolean = loading || advanceLoading;

  const permissaoBotaoCTA = () => {
    const byPassAdiantamentoUsuario = !!loggedUser?.isCashAdvanceAvailable;
    const funcionalideDisponivel = !!loggedUser?.cashAdvanceFeatureAvailable;
    const artistaElegivel = canAskAdvance(cashAdvanceAvailable);
    const campanhaEspecialBloqueada =
      specialCampaign?.approval === ClientSpecialCampaignApprovalEnum.Pending;

    return (
      byPassAdiantamentoUsuario ||
      (funcionalideDisponivel && artistaElegivel && !campanhaEspecialBloqueada)
    );
  };

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Advance.S4A_ADVANCE_INDEX,
    });
  }, []);

  return (
    <AdvanceWrapper>
      <motion.aside
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
        viewport={{ once: true }}
      >
        <ArtistFeatureCard
          isPageInfo={true}
          title={t("pages.Adiantamentos.titulo")}
          subtitle={t("pages.Adiantamentos.subtitulo")}
          image={LogoAdvance}
        />
      </motion.aside>
      <div>
        <div className="content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, type: "spring", bounce: 0.3 }}
            viewport={{ once: true }}
            className="advance-card"
          >
            <h1>
              <Trans i18nKey={t(`pages.Adiantamentos.apresentacao`)} />
            </h1>
            <p>
              <Trans i18nKey={t(`pages.Adiantamentos.texto`)} />
            </p>
            <div className="separator" />
            {!permissaoBotaoCTA() && (
              <div className="pending-info">
                <Clock style={{ flex: 2 }} />
                <span style={{ flex: 20 }}>
                  <Trans i18nKey={mensagemSemAcesso()} />
                </span>
              </div>
            )}
            {(loggedUser?.isCashAdvanceAvailable ||
              canAskAdvance(cashAdvanceAvailable) ||
              carregando) && (
              <div className="side-by-side ">
                <div>
                  <h2 className="avaiable-amount ">
                    <Trans
                      i18nKey={
                        loading || advanceLoading
                          ? t("pages.Adiantamentos.calculandoValores")
                          : t("pages.Adiantamentos.disponivel")
                      }
                    />
                  </h2>
                  <div className={`box-money ${noQuiz && "blur"}`}>
                    <div
                      className={`money ${
                        loading || advanceLoading ? "loading" : ""
                      }`}
                    >
                      {userArtist?.cashAdvanceEstimated?.trancheFactor > 1 &&
                        `${userArtist?.cashAdvanceEstimated?.trancheFactor}x `}
                      {advanceAmount}
                      {` `}
                      {!userArtist.cashAdvanceEstimated?.calculatedOn &&
                        userArtist?.cashAdvanceEstimated?.trancheFactor ===
                          1 && <>*</>}
                    </div>
                    <div className="values-note">
                      {t("pages.Adiantamentos.valoresSujeitoAlteracoes")}
                    </div>
                    {userArtist?.cashAdvanceEstimated?.trancheFactor > 1 && (
                      <>
                        <span
                          className={`money-format ${
                            loading || advanceLoading ? "loading" : ""
                          }`}
                        >
                          ({moneyFormatObject(userArtist?.cashAdvanceEstimated)}
                          )
                          {!userArtist.cashAdvanceEstimated?.calculatedOn && (
                            <>*</>
                          )}
                        </span>
                        <span style={{ display: "block", marginTop: 8 }}>
                          {t("pages.Adiantamentos.recebeParcela", {
                            amount: advanceAmount,
                          })}
                        </span>
                      </>
                    )}
                    {/* {userArtist.cashAdvanceEstimated?.calculatedOn && (
                      <p style={{ marginTop: ".6rem" }}>
                        {t("pages.Adiantamentos.disclaimerCalculadoEm", {
                          data: formatDateByLanguage(
                            userArtist.cashAdvanceEstimated.calculatedOn,
                            i18n.language,
                          ),
                          interpolation: { escapeValue: false },
                        })}
                        <br />
                        {t("pages.Adiantamentos.disclaimerValidade")}
                      </p>
                    )} */}
                    {!userArtist.cashAdvanceEstimated?.calculatedOn && (
                      <span className="values-note">
                        {t("pages.Adiantamentos.valoresSujeitoAlteracoes")}
                      </span>
                    )}
                  </div>
                </div>
                {permissaoBotaoCTA() && (
                  <div style={{ marginTop: "1.3rem", flexShrink: 0 }}>
                    <button
                      className="button"
                      disabled={loading || advanceLoading}
                      onClick={onClickIniciaProcesso}
                    >
                      {t("pages.Adiantamentos.solicitarAgora")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              type: "spring",
              bounce: 0.3,
              delay: 0.2,
            }}
            viewport={{ once: true }}
            className="streaming-revenue"
          >
            <h2>{t("pages.Adiantamentos.comoAdiantamentoFunciona")}</h2>
            <div className="how-it-works">
              <div className="step">
                <div className="image">
                  <AdiantamentoPassoDois />
                </div>
                <p>
                  <Trans i18nKey={t("pages.Adiantamentos.funcionamentoDois")} />
                </p>
              </div>
              <div className="step">
                <div className="image">
                  <AdiantamentoPassoUm />
                </div>
                <p>
                  <Trans i18nKey={t("pages.Adiantamentos.funcionamentoUm")} />
                </p>
              </div>
              <div className="step">
                <div className="image">
                  <AdiantamentoPassoTres />
                </div>
                <p>
                  <Trans i18nKey={t("pages.Adiantamentos.funcionamentoTres")} />
                </p>
              </div>
              <div className="step">
                <div className="image">
                  <AdiantamentoPassoQuatro />
                </div>
                <p>
                  <Trans
                    i18nKey={t("pages.Adiantamentos.funcionamentoQuatro")}
                  />
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              type: "spring",
              bounce: 0.3,
              delay: 0.4,
            }}
            viewport={{ once: true }}
          >
            <img
              className="image-section"
              src={ImagemSessaoIntermediaria}
              alt="imagem sessao intermediaria"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              type: "spring",
              bounce: 0.3,
              delay: 0.0,
            }}
            viewport={{ once: true }}
            className="perks"
          >
            <h2>{t("pages.Adiantamentos.beneficios")}</h2>
            <ul>
              <li>
                <img src={PurpleCircle} alt="icon" />
                {t("pages.Adiantamentos.adiantamentoInicioHighlightUm")}
              </li>
              <li>
                <img src={PurpleCircle} alt="icon" />
                {t("pages.Adiantamentos.adiantamentoInicioHighlightDois")}
              </li>
              <li>
                <img src={PurpleCircle} alt="icon" />
                {t("pages.Adiantamentos.adiantamentoInicioHighlightTres")}
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              type: "spring",
              bounce: 0.3,
              delay: 0,
            }}
            viewport={{ once: true }}
          >
            <div
              style={{
                marginTop: 32,
              }}
            >
              {permissaoBotaoCTA() && (
                <div>
                  <button
                    className="botao-roxo"
                    disabled={loading || advanceLoading}
                    onClick={onClickIniciaProcesso}
                  >
                    {t("pages.Adiantamentos.solicitarAgora")}
                  </button>
                </div>
              )}
              <h3 className="cant-bring-music">
                <Trans i18nKey={"pages.Adiantamentos.advanceOptions"} />
              </h3>
              <div className="talk-to-consultants">
                <Trans i18nKey={"pages.Adiantamentos.faleConsultores"} />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AdvanceWrapper>
  );
};

export default AdiantamentoInicio;
